import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

import { GRID_COLUMNS, Plot, clampCols } from './plot.models';
import { formatPystQuery } from './pyst-format';
import { QueryResultComponent, QueryRunStatus } from './query-result.component';

@Component({
    selector: 'app-plot-card',
    templateUrl: './plot-card.component.html',
    styleUrls: ['./plot-card.component.scss'],
})
export class PlotCardComponent implements AfterViewInit, OnChanges, OnDestroy {
    /** Owns fetching and rendering; the card owns everything around it. */
    @ViewChild(QueryResultComponent) private result?: QueryResultComponent;

    @Input() plot: Plot;
    @Output() edit = new EventEmitter<Plot>();
    /** Reopen the guided builder on this plot's saved selection. */
    @Output() editStreams = new EventEmitter<Plot>();
    @Output() remove = new EventEmitter<Plot>();
    /** The user asked for a different grid span; the parent persists it. */
    @Output() colsChanged = new EventEmitter<{ plot: Plot; cols: number }>();

    public busy = false;
    public meta = 'not fetched';
    public failed = false;
    /** The fetched document lacked pyst's embed reset, so the viewer applied one. */
    public viewerFitted = false;
    /** Back face showing: the query instead of the plot. */
    public flipped = false;
    public copied = false;
    private copiedTimer?: ReturnType<typeof setTimeout>;

    constructor() {}

    ngAfterViewInit(): void {
        // The result view has to exist before it can be told to run.
        void this.fetch();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Saving anything replaces the plot object, but only a new query needs a new
        // fetch: a width or height change just reflows the frame, and plotly follows.
        const change = changes['plot'];
        if (change && !change.firstChange && (change.previousValue as Plot)?.query !== this.plot.query) {
            void this.fetch();
        }
    }

    ngOnDestroy(): void {
        if (this.copiedTimer) {
            clearTimeout(this.copiedTimer);
        }
    }

    /** The result view reports what the run cost, or why it failed. */
    onStatus(status: QueryRunStatus): void {
        this.meta = status.text;
        this.failed = status.failed;
        this.viewerFitted = status.fitted;
    }

    flip(): void {
        this.flipped = !this.flipped;
    }

    async copyQuery(): Promise<void> {
        try {
            await navigator.clipboard.writeText(this.formattedQuery);
        } catch (error) {
            // Clipboard access can be refused (insecure context, permissions); the
            // query is still visible to select by hand, so just don't claim success.
            console.warn('Could not copy the query', error);
            return;
        }
        this.copied = true;
        if (this.copiedTimer) {
            clearTimeout(this.copiedTimer);
        }
        this.copiedTimer = setTimeout(() => (this.copied = false), 1500);
    }

    /** The stored query, pretty-printed for the back of the card. */
    get formattedQuery(): string {
        return formatPystQuery(this.plot.query);
    }

    get canNarrow(): boolean {
        return this.plot.cols > 1;
    }

    get canWiden(): boolean {
        return this.plot.cols < GRID_COLUMNS;
    }

    /** Grow or shrink by one grid column. */
    resize(delta: number): void {
        const cols = clampCols(this.plot.cols + delta, this.plot.cols);
        if (cols !== this.plot.cols) {
            this.colsChanged.emit({ plot: this.plot, cols });
        }
    }

    /** Run the plot's query again; the result view owns the rest. */
    async fetch(): Promise<void> {
        this.busy = true;
        try {
            await this.result?.run();
        } finally {
            this.busy = false;
        }
    }


}
